/**
 * Economic-position cascade — spawnable, named custom-calculation cards.
 *
 * A lifetime financial waterfall: sales / corp-funding / goods / corrections
 * / other → Net P&L → −open debts → Net of receivables → +revenue on stock →
 * Total economic (excl. Pfand) → Pfand committed/returned → Pfand net →
 * Total economic position.
 *
 * Each card has TWO independent per-row toggles in edit mode:
 *   • Show  — hide the row from view (display only; the maths is unaffected,
 *             and a run of hidden rows BETWEEN two shown rows becomes a
 *             dotted rule — leading/trailing hidden runs render nothing).
 *   • Calc  — include/exclude the row from the calculation (addend rows
 *             only; subtotals are derived). Excluding any addend recomputes
 *             every subtotal from the included set, renders the excluded
 *             row's value in (brackets), shows a "Custom" badge, and relabels
 *             the final row to "Total custom calculation".
 *
 * Cards are named (editable title), addable, and deletable; the whole set is
 * persisted server-side via the ``ui_preference`` store (shared row) so it
 * survives across devices. No dedicated backend: the figures are assembled
 * from the ``/expenses/timeline`` response (sums + per-source rows, "Other
 * expenses" as the residual) plus ``/dashboard/pfand-balance``.
 */

import { useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import type { PfandBalanceResponse, TimelineResponse } from "../api/client";
import { useUiPreference } from "../hooks/useUiPreference";

const { Text } = Typography;

const PREF_KEY = "dashboard.economic_cards";

/** Per-card persisted config. ``hidden`` = row ids removed from display;
 * ``excluded`` = addend ids removed from the calculation. */
interface CardConfig {
  id: string;
  title: string;
  hidden: string[];
  excluded: string[];
}

const DEFAULT_CARDS: CardConfig[] = [
  { id: "default", title: "Economic position", hidden: [], excluded: [] },
];

type RowKind = "addend" | "subtotal" | "total";

interface DisplayRow {
  id: string;
  label: string;
  cents: number;
  kind: RowKind;
  indent?: boolean;
  /** 0051: hide this row entirely when ``cents == 0``. Used for the
   * "Others (not integrated)" failover residual so the card stays
   * tidy when every expense rolls into a named bucket. */
  hideWhenZero?: boolean;
}

/** Addend ids that the Calc column can toggle (subtotals/total are derived). */
const ADDEND_IDS = [
  "sales",
  "corp_funding",
  "goods",
  "corrections",
  // 0051: shrinkage attribution — Stock losses (unexplained) +
  // Stock broken (operator-explained). Both negative. Surfaced
  // separately so the bar manager reads explained vs unexplained
  // loss at a glance.
  "stock_loss",
  "stock_broken",
  // Volunteer compensation — a REAL labour expense (the volunteer's drinks are
  // real sales; the comp is the labour cost that nets the forgiven tab). Already
  // inside Net P&L; extracted here as a named addend so it counts exactly once.
  "volunteer_comp",
  // Failover residual: anything that doesn't roll into the named
  // categories above lands here. Hidden by RowLine when cents == 0
  // (no leftover) so the card stays tidy when every expense is
  // accounted for.
  "other_expenses",
  "open_debts",
  "revenue_on_stock",
  "pfand_committed",
  "pfand_returned",
] as const;

function euro(cents: number, signed: boolean): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    signDisplay: signed ? "always" : "auto",
  }).format(cents / 100);
}

/** The nine signed addend values, derived once from the data. */
function computeAddends(timeline: TimelineResponse, pfand: PfandBalanceResponse) {
  let sales = 0;
  let corrections = 0;
  let procurement = 0; // negative (cost outflow)
  for (const r of timeline.rows) {
    if (r.source === "sales") sales += r.value_cents;
    else if (r.source === "correction") corrections += r.value_cents;
    else if (r.source === "procurement") procurement += r.value_cents;
  }
  const goods = -procurement; // positive magnitude
  const corpFunding = timeline.sums.corporate_funding_cents;
  const netPnlFull = timeline.sums.value_p_and_l_cents;
  // 0051: shrinkage attribution. Backend ships ABS magnitudes; we
  // flip the sign here so the card renders them as deductions.
  const stockLoss = -timeline.sums.stock_loss_cents;
  const stockBroken = -timeline.sums.stock_broken_cents;
  // Volunteer compensation — a real labour expense already inside Net P&L. Like
  // stock_loss/broken, the backend ships an ABS magnitude; flip the sign so the
  // card renders it as a deduction, and subtract it from the residual below so
  // it is counted exactly once.
  const volunteerComp = -timeline.sums.volunteer_comp_cents;
  // Residual = whatever Net P&L is left after sales, corp funding,
  // goods, corrections, stock_loss, stock_broken, and volunteer comp are
  // subtracted off. When every expense lands in a named bucket this is zero and
  // the row hides itself; non-zero means "Others (not integrated)" —
  // an unmatched expense the card hadn't anticipated.
  const otherExpenses =
    netPnlFull
    - sales
    - corpFunding
    + goods
    + corrections
    - stockLoss
    - stockBroken
    - volunteerComp;
  return {
    sales, // +
    corp_funding: corpFunding, // +
    goods: -goods, // − (display signed)
    corrections: -corrections, // −
    stock_loss: stockLoss, // − (signed)
    stock_broken: stockBroken, // − (signed)
    volunteer_comp: volunteerComp, // − (real labour expense, signed)
    other_expenses: otherExpenses, // − (residual, signed)
    open_debts: -timeline.sums.open_user_debts_cents, // −
    revenue_on_stock: timeline.sums.revenue_on_stock_cents, // +
    pfand_committed: -pfand.received_cents, // −
    pfand_returned: pfand.returned_cents, // +
  };
}

/** Build the cascade with subtotals recomputed from the included addends. */
function buildRows(
  timeline: TimelineResponse,
  pfand: PfandBalanceResponse,
  excluded: Set<string>,
): { rows: DisplayRow[]; custom: boolean } {
  const a = computeAddends(timeline, pfand);
  const v = (id: keyof typeof a): number => (excluded.has(id) ? 0 : a[id]);

  const netPnl =
    v("sales")
    + v("corp_funding")
    + v("goods")
    + v("corrections")
    + v("stock_loss")
    + v("stock_broken")
    + v("volunteer_comp")
    + v("other_expenses");
  const netReceivables = netPnl + v("open_debts");
  const totalExcl = netReceivables + v("revenue_on_stock");
  const pfandNet = v("pfand_committed") + v("pfand_returned");
  const total = totalExcl + pfandNet;
  const custom = ADDEND_IDS.some((id) => excluded.has(id));

  const rows: DisplayRow[] = [
    { id: "sales", label: "Strichliste sales", cents: a.sales, kind: "addend", indent: true },
    { id: "corp_funding", label: "Corporate funding", cents: a.corp_funding, kind: "addend", indent: true },
    { id: "goods", label: "Goods cost", cents: a.goods, kind: "addend", indent: true },
    { id: "corrections", label: "Corrections", cents: a.corrections, kind: "addend", indent: true },
    { id: "stock_loss", label: "Stock losses", cents: a.stock_loss, kind: "addend", indent: true },
    { id: "stock_broken", label: "Stock broken", cents: a.stock_broken, kind: "addend", indent: true },
    { id: "volunteer_comp", label: "Volunteer compensation", cents: a.volunteer_comp, kind: "addend", indent: true, hideWhenZero: true },
    {
      id: "other_expenses",
      // 0051: rename + auto-hide. The card lists every named bucket
      // above; this row appears only when the named buckets don't
      // fully account for Net P&L — i.e. an expense category landed
      // that the card hasn't been taught yet. Helps catch silent
      // category-additions before they pollute reporting.
      label: "Others (not integrated)",
      cents: a.other_expenses,
      kind: "addend",
      indent: true,
      hideWhenZero: true,
    },
    { id: "net_pnl", label: "Net P&L", cents: netPnl, kind: "subtotal" },
    { id: "open_debts", label: "Open user debts", cents: a.open_debts, kind: "addend" },
    { id: "net_receivables", label: "Net of receivables", cents: netReceivables, kind: "subtotal" },
    { id: "revenue_on_stock", label: "Revenue on stock", cents: a.revenue_on_stock, kind: "addend" },
    { id: "total_excl_pfand", label: "Total economic (excl. Pfand)", cents: totalExcl, kind: "subtotal" },
    { id: "pfand_committed", label: "Pfand committed", cents: a.pfand_committed, kind: "addend" },
    { id: "pfand_returned", label: "Pfand returned", cents: a.pfand_returned, kind: "addend" },
    { id: "pfand_net", label: "Pfand net", cents: pfandNet, kind: "subtotal" },
    {
      id: "total_incl_pfand",
      label: custom ? "Total custom calculation" : "Total economic position",
      cents: total,
      kind: "total",
    },
  ];
  return { rows, custom };
}

function RowLine({
  row,
  editing,
  hidden,
  excluded,
  onToggleShow,
  onToggleCalc,
}: {
  row: DisplayRow;
  editing: boolean;
  hidden: boolean;
  excluded: boolean;
  onToggleShow: (id: string) => void;
  onToggleCalc: (id: string) => void;
}) {
  const isTotal = row.kind === "total";
  const isSub = row.kind === "subtotal";
  const isAddend = row.kind === "addend";
  const strong = isTotal || isSub;
  const valueColor = strong
    ? row.cents < 0
      ? "#cf1322"
      : "#16a34a"
    : "var(--ant-color-text-secondary, #595959)";
  const valueText = euro(row.cents, isAddend);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 0",
        paddingLeft: row.indent ? 22 : 0,
        borderTop: isTotal
          ? "2px solid var(--ant-color-border, #d9d9d9)"
          : isSub
            ? "1px solid var(--ant-color-split, rgba(0,0,0,0.06))"
            : undefined,
        opacity: editing && hidden ? 0.45 : 1,
      }}
    >
      {editing && (
        <Space size={4} style={{ width: 56 }}>
          <Checkbox
            checked={!hidden}
            onChange={() => onToggleShow(row.id)}
            aria-label={`Show ${row.label}`}
          />
          {isAddend ? (
            <Checkbox
              checked={!excluded}
              onChange={() => onToggleCalc(row.id)}
              aria-label={`Include ${row.label} in calculation`}
            />
          ) : (
            <span style={{ width: 16, display: "inline-block" }} />
          )}
        </Space>
      )}
      <Text strong={strong} style={{ flex: 1, fontSize: isTotal ? 15 : 14 }}>
        {strong ? "= " : ""}
        {row.label}
      </Text>
      <Text
        strong={strong}
        style={{
          color: valueColor,
          fontVariantNumeric: "tabular-nums",
          fontSize: isTotal ? 16 : 14,
          whiteSpace: "nowrap",
          // Excluded-from-calc addend: show its value in brackets, dimmed.
          opacity: isAddend && excluded ? 0.5 : 1,
        }}
      >
        {isAddend && excluded ? `(${valueText})` : valueText}
      </Text>
    </div>
  );
}

function EconomicCard({
  config,
  timeline,
  pfand,
  onChange,
  onDelete,
}: {
  config: CardConfig;
  timeline: TimelineResponse | null;
  pfand: PfandBalanceResponse | null;
  onChange: (next: CardConfig) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const hidden = useMemo(() => new Set(config.hidden), [config.hidden]);
  const excluded = useMemo(() => new Set(config.excluded), [config.excluded]);

  const ready = !!timeline && !!pfand && Array.isArray(timeline.rows);
  const { rows, custom } = useMemo(
    () =>
      ready
        ? buildRows(timeline as TimelineResponse, pfand as PfandBalanceResponse, excluded)
        : { rows: [] as DisplayRow[], custom: false },
    [ready, timeline, pfand, excluded],
  );

  const toggleIn = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const onToggleShow = (id: string) =>
    onChange({ ...config, hidden: toggleIn(config.hidden, id) });
  const onToggleCalc = (id: string) =>
    onChange({ ...config, excluded: toggleIn(config.excluded, id) });

  // 0051: drop the "Others (not integrated)" residual when its value is
  // zero so the card stays tidy in the common case (every expense
  // accounted for by a named bucket).
  const visibleRows = rows.filter(
    (r) => !(r.hideWhenZero === true && r.cents === 0),
  );

  // Display walk: dotted rule only for a hidden run BETWEEN two visible rows.
  const rendered: JSX.Element[] = [];
  if (editing) {
    for (const row of visibleRows) {
      rendered.push(
        <RowLine
          key={row.id}
          row={row}
          editing
          hidden={hidden.has(row.id)}
          excluded={excluded.has(row.id)}
          onToggleShow={onToggleShow}
          onToggleCalc={onToggleCalc}
        />,
      );
    }
  } else {
    let pendingHidden = false;
    let emittedAny = false;
    for (const row of visibleRows) {
      if (hidden.has(row.id)) {
        pendingHidden = true;
        continue;
      }
      if (pendingHidden && emittedAny) {
        rendered.push(
          <div
            key={`gap-${row.id}`}
            aria-hidden
            style={{
              borderTop: "3px dotted var(--ant-color-border, #d9d9d9)",
              margin: "6px 0",
            }}
          />,
        );
      }
      rendered.push(
        <RowLine
          key={row.id}
          row={row}
          editing={false}
          hidden={false}
          excluded={excluded.has(row.id)}
          onToggleShow={onToggleShow}
          onToggleCalc={onToggleCalc}
        />,
      );
      emittedAny = true;
      pendingHidden = false;
    }
  }

  return (
    <Card
      size="small"
      loading={!ready}
      title={
        <Space>
          <Text
            strong
            editable={
              editing
                ? { onChange: (t) => onChange({ ...config, title: t || "Untitled" }) }
                : false
            }
          >
            {config.title}
          </Text>
          {custom && <Tag color="orange">Custom</Tag>}
        </Space>
      }
      extra={
        <Space size={4}>
          {editing && (
            <Popconfirm
              title="Delete this card?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={onDelete}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
          <Button
            type="text"
            size="small"
            icon={editing ? <CheckOutlined /> : <EditOutlined />}
            onClick={() => setEditing((e) => !e)}
          >
            {editing ? "Done" : "Edit"}
          </Button>
        </Space>
      }
    >
      {editing && (
        <div style={{ display: "flex", gap: 8, paddingBottom: 6, marginBottom: 2 }}>
          <Text type="secondary" style={{ width: 28, fontSize: 11 }}>
            Show
          </Text>
          <Text type="secondary" style={{ width: 28, fontSize: 11 }}>
            Calc
          </Text>
        </div>
      )}
      {rows.length > 0 && <div>{rendered}</div>}
      {editing && (
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
          Show hides a row (totals still count it); Calc removes an addend from
          the maths (its value shows in brackets, and the result becomes a
          custom calculation).
        </Text>
      )}
    </Card>
  );
}

export function EconomicPositionCards({
  timeline,
  pfand,
}: {
  timeline: TimelineResponse | null;
  pfand: PfandBalanceResponse | null;
}) {
  const { value: cards, setValue: setCards } = useUiPreference<CardConfig[]>(
    PREF_KEY,
    DEFAULT_CARDS,
  );

  const updateCard = (id: string, next: CardConfig) =>
    setCards(cards.map((c) => (c.id === id ? next : c)));
  const deleteCard = (id: string) => setCards(cards.filter((c) => c.id !== id));
  const addCard = () =>
    setCards([
      ...cards,
      {
        // Date.now keeps ids unique without a counter; cards are low-volume.
        id: `card-${Date.now()}`,
        title: `Custom calculation ${cards.length + 1}`,
        hidden: [],
        excluded: [],
      },
    ]);

  return (
    <Row gutter={[16, 16]}>
      {cards.map((c) => (
        <Col xs={24} md={12} lg={10} key={c.id}>
          <EconomicCard
            config={c}
            timeline={timeline}
            pfand={pfand}
            onChange={(next) => updateCard(c.id, next)}
            onDelete={() => deleteCard(c.id)}
          />
        </Col>
      ))}
      <Col xs={24} md={12} lg={10}>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addCard}
          style={{ width: "100%", height: 48 }}
        >
          Add calculation
        </Button>
      </Col>
    </Row>
  );
}
